import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as pdfjs from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { PublicSignView } from '@todo/shared';
import { ApiError } from '../api/client';
import { signApi } from '../api/sign.api';
import { SignaturePad } from '../components/SignaturePad';
import { renderPdfPages } from '../lib/pdf-render';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

/**
 * The document itself, rendered page by page with pdf.js, with amber
 * "Sign here" markers at THIS recipient's placed boxes - the browser's
 * native PDF viewer cannot overlay anything, hence the own renderer.
 */
function DocumentView({
  token,
  fields,
  signedOverlays,
  recipientName,
  comments,
  onComment,
  onSignHere,
}: {
  token: string;
  fields: PublicSignView['fields'];
  signedOverlays: PublicSignView['signedOverlays'];
  /** Named ON the box, not just in the header: a payment approval has three
   *  signature blocks and the signer has to know WHICH one is theirs. */
  recipientName: string;
  /** Text typed into each placed COMMENT box, keyed by field id. */
  comments: Record<string, string>;
  onComment: (fieldId: string, value: string) => void;
  /**
   * Clicking your own signature box jumps to the pad below the preview.
   *
   * The box on the page is where the signer LOOKS - it is labelled "sign
   * here" - but it is not where they can actually sign; the pad is further
   * down, often below the fold. Without this they see the instruction and
   * have nothing to do with it.
   *
   * UNDEFINED when there is nothing to scroll to - the pad only exists
   * while this person can actually sign. A recipient waiting their turn
   * still sees their boxes on the page; making those boxes click and do
   * nothing would read as a broken button, so they stay inert and keep
   * the plain cursor.
   */
  onSignHere?: () => void;
}) {
  const [pageCount, setPageCount] = useState(0);
  const [failed, setFailed] = useState(false);
  const pdfRef = useRef<pdfjs.PDFDocumentProxy | null>(null);
  const canvasRefs = useRef(new Map<number, HTMLCanvasElement>());
  /** The scroll box, and the signer's own FIRST box inside it. */
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const firstFieldRef = useRef<HTMLDivElement | null>(null);
  /** Scroll once per document. Re-scrolling on every re-render would yank
   *  the page back while somebody is reading it. */
  const scrolled = useRef(false);

  /**
   * The signer's EARLIEST box - lowest page, then highest on that page.
   *
   * Not simply fields[0]: the array arrives in whatever order the API
   * returns, which is placement order, not document order. A sender who
   * placed the page-5 box before the page-2 one would otherwise send every
   * signer to page 5 and past the box they need.
   */
  const firstField = fields.length === 0
    ? null
    : fields.reduce((a, b) => (a.page !== b.page ? (a.page < b.page ? a : b) : (a.y <= b.y ? a : b)));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const bytes = await signApi.publicFileBytes(token);
        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        if (cancelled) return;
        pdfRef.current = pdf;
        setPageCount(pdf.numPages);
      } catch {
        setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    const pdf = pdfRef.current;
    if (!pdf || pageCount === 0) return;
    // Fluid: the signer's column is narrow on a phone, but the canvas is
    // still DRAWN wide, so text stays sharp when they pinch-zoom.
    const job = renderPdfPages(pdf, (n) => canvasRefs.current.get(n), 1000, true);
    return () => job.cancel();
  }, [pageCount]);

  /**
   * Put the signer in front of their own box.
   *
   * A payment approval runs to several pages and the signature block is
   * usually near the end, so opening at page 1 means hunting for the one
   * amber box that belongs to you. This scrolls the preview to it once the
   * pages have rendered.
   *
   * WHY A FRAME LATER: the canvases get their real height inside the render
   * loop above, and a box positioned in percentages has no meaningful
   * offsetTop until they do. Measuring in the same tick scrolls to roughly
   * zero. requestAnimationFrame waits for that layout.
   *
   * Scrolls the CONTAINER, not the window: scrollIntoView() would drag the
   * whole page and push the signature pad off screen, which is the thing
   * they need next.
   */
  useEffect(() => {
    if (pageCount === 0 || scrolled.current) return;

    const frame = requestAnimationFrame(() => {
      const box = firstFieldRef.current;
      const scroller = scrollRef.current;
      if (!box || !scroller) return;

      // A little above the box, so it does not sit flush against the top
      // edge with no context about what it is on.
      const offset = box.offsetTop - scroller.clientHeight / 3;
      scroller.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
      scrolled.current = true;
    });

    return () => cancelAnimationFrame(frame);
  }, [pageCount, firstField]);

  if (failed) {
    return (
      <p className="border-b border-slate-100 px-6 py-8 text-center text-sm text-slate-500">
        Could not display the document preview.
      </p>
    );
  }

  return (
    <div ref={scrollRef} className="max-h-[65vh] overflow-y-auto border-b border-slate-100 bg-slate-50 p-4">
      {pageCount === 0 && (
        <p className="py-10 text-center text-sm text-slate-400">Loading document…</p>
      )}
      <div className="mx-auto max-w-[700px] space-y-4">
        {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
          <div key={n} className="relative border border-slate-200 bg-white shadow-sm">
            <canvas ref={(el) => { if (el) canvasRefs.current.set(n, el); }} className="block w-full" />
            {/* What earlier signers left in place: their signature image
                in a signature box, the text they typed in a comment box.
                Both matter to the person signing now - a comment from the
                signer above is often the reason this one is being asked. */}
            {signedOverlays
              .filter((o) => o.page === n)
              .map((o, i) => {
                const box = {
                  left: `${o.x * 100}%`,
                  top: `${o.y * 100}%`,
                  width: `${o.width * 100}%`,
                  height: `${o.height * 100}%`,
                };
                return o.signatureUrl ? (
                  <img
                    key={`signed-${i}`}
                    src={o.signatureUrl}
                    alt="Signature"
                    style={box}
                    className="pointer-events-none absolute object-contain"
                  />
                ) : (
                  <div
                    key={`signed-${i}`}
                    style={box}
                    title={o.valueText ?? undefined}
                    className="pointer-events-none absolute overflow-hidden rounded border border-sky-300 bg-sky-50/80 px-1 py-0.5 text-[10px] leading-snug text-slate-800"
                  >
                    {o.valueText}
                  </div>
                );
              })}
            {fields
              .filter((f) => f.page === n)
              .map((f, i) => (
                <div
                  key={i}
                  /* The anchor the scroll effect aims at: the signer's
                     earliest box in DOCUMENT order (see firstField) - not
                     the first in the array, and not the first on this
                     page, since fields may start on page 3. */
                  ref={f === firstField ? firstFieldRef : undefined}
                  style={{
                    left: `${f.x * 100}%`,
                    top: `${f.y * 100}%`,
                    width: `${f.width * 100}%`,
                    height: `${f.height * 100}%`,
                  }}
                  /* A signature box is now a BUTTON in effect - clicking it
                     scrolls to the pad. A comment box is typed into in
                     place, so it keeps its own click. */
                  onClick={f.kind === 'COMMENT' ? undefined : onSignHere}
                  title={f.kind === 'COMMENT' || !onSignHere ? undefined : 'Go to the signature pad'}
                  className={[
                    'absolute flex items-center justify-center rounded border-2',
                    f.kind === 'COMMENT'
                      // z-10: a comment box that overlaps a signature box
                      // must still receive the click, or it cannot be
                      // typed into at all.
                      ? 'z-10 border-sky-500 bg-sky-100/60'
                      // Was pointer-events-none throughout. Clickable only
                      // when there is a pad to reach, and the cursor says
                      // which of the two states this is before the click.
                      : onSignHere
                        ? 'cursor-pointer border-dashed border-amber-500 bg-amber-200/40 transition hover:bg-amber-200/70'
                        : 'pointer-events-none border-dashed border-amber-500 bg-amber-200/40',
                  ].join(' ')}
                >
                  {f.kind === 'COMMENT' ? (
                    // Typed in place, on the page, where it will be
                    // printed - so the signer can see it fits the box
                    // before committing to it.
                    <>
                      {/* A caption riding above the box. Without it a
                          comment box and a signature box are two
                          rectangles on a page, and the signer has to
                          click one to find out which is which. */}
                      <span className="pointer-events-none absolute -top-4 left-0 whitespace-nowrap rounded bg-sky-600 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-white">
                        Comment {f.required ? '(required)' : '(optional)'}
                      </span>
                      <textarea
                        value={comments[f.id] ?? ''}
                        onChange={(e) => onComment(f.id, e.target.value)}
                        maxLength={500}
                        placeholder={f.required ? 'Type your comment (required)' : 'Type a comment (optional)'}
                        aria-label={f.required ? 'Required comment' : 'Optional comment'}
                        className={[
                          'h-full w-full resize-none rounded bg-white px-1.5 py-1 text-[11px] leading-snug outline-none',
                          f.required && !(comments[f.id] ?? '').trim()
                            ? 'ring-2 ring-red-400'
                            : 'ring-1 ring-sky-400 focus:ring-2 focus:ring-sky-500',
                        ].join(' ')}
                      />
                    </>
                  ) : (
                    /* The name comes FIRST - it is the part that answers
                       "is this mine?". `truncate` keeps a long name from
                       bursting a narrow box, and the title attribute keeps
                       it readable on hover when it does get cut. */
                    <span
                      title={`${recipientName} - sign here`}
                      className="max-w-full truncate rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                    >
                      {recipientName} · Sign here
                    </span>
                  )}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** "22 Sep 2026, 15:13" in the viewer's own locale and zone. */
function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    .format(new Date(iso));
}

/**
 * The PUBLIC signing page - /sign/:token, no login. The token in the URL
 * is the recipient's authentication; everything renders from the one
 * publicView call. Signature is drawn (or typed) on a canvas and submitted
 * as a PNG data URL.
 */

export default function PublicSignPage() {
  const { token = '' } = useParams();
  const [view, setView] = useState<PublicSignView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'signed' | 'declined' | 'delegated' | null>(null);
  const navigate = useNavigate();
  /** True = sign with the profile signature the API advertised. */
  const [useSaved, setUseSaved] = useState(false);

  /**
   * The signing panel below the preview - what "sign here" on the page
   * points at.
   *
   * scrollIntoView on the WINDOW is right here, unlike the in-preview
   * scroll: the pad is a sibling further down the page, not inside the
   * preview's own scroll box, so the page itself has to move.
   */
  const padRef = useRef<HTMLDivElement | null>(null);

  const goToPad = useCallback(() => {
    const pad = padRef.current;
    if (!pad) return;
    pad.scrollIntoView({ behavior: 'smooth', block: 'center' });
    /*
     * A nudge of attention after the scroll, because the pad looks much
     * like the rest of the panel and a smooth scroll gives no sense of
     * arrival. Focusing the first control would be stronger, but on a
     * canvas pad there is nothing to focus, and on mobile focusing an
     * input opens the keyboard over the thing we just scrolled to.
     */
    pad.classList.add('ring-2', 'ring-brand-400');
    window.setTimeout(() => pad.classList.remove('ring-2', 'ring-brand-400'), 1200);
  }, []);
  /** Consent must be explicit - the certificate reports when it was given. */
  const [agreed, setAgreed] = useState(false);
  /** Text for each placed COMMENT box, keyed by field id. */
  const [comments, setComments] = useState<Record<string, string>>({});
  /** Handing the signature to a colleague. */
  const [delegating, setDelegating] = useState(false);
  const [delegateTo, setDelegateTo] = useState('');
  const [delegateReason, setDelegateReason] = useState('');

  useEffect(() => {
    signApi.publicView(token)
      .then(setView)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'This signing link is not valid.'))
      .finally(() => setLoading(false));
  }, [token]);

  /**
   * The electronic-signature consent. A tick, not a paragraph nobody has
   * to acknowledge: the Certificate of Completion reports "Terms agreed
   * on", and it can only report what was actually captured.
   */
  async function submit() {
    if (!useSaved && !signature) return;
    if (!agreed) return;
    setBusy(true);
    setError(null);
    try {
      await signApi.sign(token, {
        ...(useSaved ? { useSavedSignature: true } : { signatureDataUrl: signature! }),
        // The consent is sent, but the TIMESTAMP is the server's: a moment
        // the signer's own browser could set is worth nothing on a
        // certificate.
        agreedToTerms: true,
        comments: Object.entries(comments)
          .filter(([, value]) => value.trim())
          .map(([fieldId, value]) => ({ fieldId, value: value.trim() })),
      });
      setDone('signed');

      /**
       * An org member goes back to CG Sign; an outsider stays here.
       *
       * Sending an external signer to /cg-sign would land them on a login
       * form they have no account for - a dead end immediately after the
       * one thing we asked them to do. The pause lets the confirmation
       * register before the page changes under them.
       */
      if (view?.isPortalMember) {
        window.setTimeout(() => navigate('/cg-sign'), 1500);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not submit your signature - try again.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Hand this signature to a colleague in the same department.
   *
   * The server re-points the recipient row and issues a fresh token, so
   * this link stops working the moment it succeeds - which is why the
   * page reloads into the "no longer yours" state rather than pretending
   * it can still sign.
   */
  async function handOver() {
    if (!delegateTo || busy) return;
    setBusy(true);
    setError(null);
    try {
      await signApi.delegate(token, {
        userId: delegateTo,
        reason: delegateReason.trim(),
      });
      setDone('delegated');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not hand this over.');
    } finally {
      setBusy(false);
    }
  }

  async function submitDecline() {
    if (!declineReason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await signApi.decline(token, { reason: declineReason.trim() });
      setDone('declined');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not decline - try again.');
    } finally {
      setBusy(false);
    }
  }

  const statusNote = view && !view.canSign && !done
    ? view.waitingForTurn
      ? 'It is not your turn yet. The document stays hidden until everyone before you has signed - you will get an email the moment it is your turn.'
      : view.recipientStatus === 'SIGNED'
        ? 'You have already signed this document.'
        : view.recipientStatus === 'DECLINED'
          ? 'You declined this document.'
          : view.documentStatus === 'COMPLETED'
            ? 'This document is fully signed.'
            : 'This document is no longer open for signing.'
    : null;

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-4 flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-slate-900 text-sm font-bold text-white">CG</span>
          <span className="text-sm font-semibold text-slate-700">CG Sign</span>
        </div>

        {loading && <p className="py-16 text-center text-sm text-slate-500">Loading…</p>}

        {!loading && !view && (
          <div className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
            <p className="text-sm text-red-600">{error ?? 'This signing link is not valid.'}</p>
          </div>
        )}

        {view && (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-6 py-4">
              <h1 className="text-lg font-semibold text-slate-900">{view.documentTitle}</h1>
              <p className="mt-0.5 text-sm text-slate-500">
                {view.ownerName}{view.orgName ? ` (${view.orgName})` : ''} has asked you ({view.recipientName}) to sign this document.
              </p>
              {/* Handed over: who passed it on, when, and why. Above the
                  sender's message because it answers the first question
                  the recipient has - "why is this mine?" - which the
                  sender's message, written for someone else, does not. */}
              {view.handedOver && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                    Passed to you for signature
                  </p>
                  <p className="mt-1 text-sm text-slate-800">
                    <span className="font-semibold">{view.handedOver.fromName}</span>
                    {view.handedOver.fromEmail && (
                      <span className="text-slate-500"> ({view.handedOver.fromEmail})</span>
                    )}{' '}
                    handed this signature to you on {formatWhen(view.handedOver.at)}.
                  </p>
                  <p className="mt-1.5 text-sm text-slate-700">
                    <span className="text-slate-500">Reason: </span>
                    {view.handedOver.reason}
                  </p>
                </div>
              )}
              {view.message && (
                <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600 ring-1 ring-slate-200">
                  “{view.message}”
                </p>
              )}
            </div>

            {view.fileUrl && (
              <DocumentView
                token={token}
                fields={view.fields}
                signedOverlays={view.signedOverlays}
                recipientName={view.recipientName}
                comments={comments}
                onComment={(id, value) =>
                  setComments((c) => ({ ...c, [id]: value }))}
                /* Only when the pad is actually rendered - see the prop. */
                onSignHere={view.canSign && !done ? goToPad : undefined}
              />
            )}

            <div className="px-6 py-5">
              {done === 'signed' && (
                <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 ring-1 ring-emerald-200">
                  Thank you - your signature has been recorded. Once everyone has signed,
                  you will receive the completed document by email.
                </p>
              )}
              {done === 'delegated' && (
                <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600 ring-1 ring-slate-200">
                  Handed over. Your colleague has been emailed their own
                  signing link, and this one no longer works.
                </p>
              )}
              {done === 'declined' && (
                <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600 ring-1 ring-slate-200">
                  You declined to sign. The sender has been informed.
                </p>
              )}
              {statusNote && (
                <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600 ring-1 ring-slate-200">
                  {statusNote}
                </p>
              )}

              {view.canSign && !done && (
                /* The scroll target for "sign here" on the page. A plain
                   div rather than the fragment that was here, because the
                   handler needs something to measure and to flash. */
                <div ref={padRef} className="rounded-xl transition-shadow">
                  {error && (
                    <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">{error}</p>
                  )}
                  {view.savedSignatureUrl && (
                    <label className="mb-3 flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={useSaved}
                        onChange={(e) => setUseSaved(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 accent-brand-600"
                      />
                      <span className="text-sm text-slate-700">Use my saved signature</span>
                      <img
                        src={view.savedSignatureUrl}
                        alt="Saved signature"
                        className="ml-auto h-10 rounded border border-slate-200 bg-white px-2"
                      />
                    </label>
                  )}
                  {!useSaved && <SignaturePad onChange={setSignature} />}
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    {declining ? (
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        <input value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                          placeholder="Why are you declining?"
                          maxLength={500}
                          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-red-400" />
                        <button type="button" disabled={busy || !declineReason.trim()} onClick={() => void submitDecline()}
                          className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                          Decline
                        </button>
                        <button type="button" onClick={() => setDeclining(false)}
                          className="text-xs font-medium text-slate-500 hover:text-slate-700">
                          Back
                        </button>
                      </span>
                    ) : delegating ? (
                      /* Handing it on, not refusing it: a different act
                         from declining, and it keeps the signature in the
                         department rather than sending the envelope back
                         to the sender to redo. */
                      <span className="flex w-full flex-wrap items-center gap-2">
                        <select
                          value={delegateTo}
                          onChange={(e) => setDelegateTo(e.target.value)}
                          aria-label="Colleague to hand this to"
                          className="min-w-52 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
                        >
                          <option value="">Choose a colleague…</option>
                          {view.delegateCandidates.map((c) => (
                            <option key={c.userId} value={c.userId}>
                              {c.displayName}{c.jobTitle ? ` · ${c.jobTitle}` : ''}
                            </option>
                          ))}
                        </select>
                        <input
                          value={delegateReason}
                          onChange={(e) => setDelegateReason(e.target.value)}
                          maxLength={300}
                          placeholder="Why are you handing it over? (required)"
                          aria-label="Reason for handing this over"
                          className="min-w-44 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
                        />
                        <button type="button"
                          disabled={busy || !delegateTo || delegateReason.trim().length < 3}
                          onClick={() => void handOver()}
                          className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900 disabled:opacity-50">
                          {busy ? 'Handing over…' : 'Hand over'}
                        </button>
                        <button type="button" onClick={() => setDelegating(false)}
                          className="text-sm font-medium text-slate-500 hover:text-slate-700">
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <>
                        <button type="button" onClick={() => setDeclining(true)}
                          className="text-sm font-medium text-red-600 hover:text-red-700">
                          Decline to sign
                        </button>
                        {view.delegateCandidates.length > 0 && (
                          /* A button, not a text link: handing the
                             signature to a colleague is one of the three
                             things you can do on this page, and it should
                             look like one. */
                          <button
                            type="button"
                            onClick={() => setDelegating(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                                 className="h-4 w-4" aria-hidden>
                              <path strokeLinecap="round" strokeLinejoin="round"
                                    d="M15 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1M8.5 7.5a3 3 0 1 0 0 .01M17 11h5m-2.5-2.5L22 11l-2.5 2.5" />
                            </svg>
                            Assign to someone else
                          </button>
                        )}
                        <button type="button" disabled={busy || !agreed || (!useSaved && !signature)} onClick={() => void submit()}
                          className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
                          {busy ? 'Signing…' : 'Sign document'}
                        </button>
                      </>
                    )}
                  </div>
                  <label className="mt-3 flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={agreed}
                      onChange={(e) => setAgreed(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0"
                    />
                    <span className="text-[11px] leading-relaxed text-slate-500">
                      I agree that my electronic signature, together with the recorded
                      time and network address, stands for my handwritten signature on
                      this document. The moment you tick this is recorded on the
                      Certificate of Completion.
                    </span>
                  </label>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
